// Host destinations the consumer home links to, in one place, so a host route change is a single edit
// here. Callers are the step catalogue (`plan-steps.tsx`) and the sidebar's "Send a message".
//
// `/appointments` HAS a constant now — CONAPP-346 added it, and CONAPP-293's reason for adding none
// has expired rather than been overruled. That page rendered mock data at the time, so a link would
// have taken a member from their real card to a fabricated one; it has read real data since MEM-855
// (`@internal/telemedicine`'s `useAppointments` queries `telemedicine.getAppointments`). The booked
// visit's card links there with **View appointment**.
//
// CONAPP-344 added `TELEMEDICINE_BOOKING_PATH` under a different thaw: that path was real all along,
// and what CONAPP-293 was waiting on was the DATA — a per-appointment forms signal, and an
// appointment id to deep-link with. Both exist now (`formsComplete` and the id on
// `telemedicine.getAppointments`), so the visit's forms card can point at a member's own
// appointment.
//
// Literal paths rather than the host's `Paths` enum: the package can't import from `#/` (one-way
// dependency — see packages/consumer/README.md).

// The sidebar's "Send a message" only, since CONAPP-192: CONAPP-234's archetype → action map has
// no Care Team step, so no card links here any more.
export const MESSAGES_PATH = "/messages"
export const TELEMEDICINE_PATH = "/telemedicine"
// The host's `Paths.TELEMEDICINE_BOOKING`, and a BASE rather than a destination: the visit's
// forms card interpolates `${TELEMEDICINE_BOOKING_PATH}/${appointmentId}/forms`, which is the same
// URL `@internal/telemedicine`'s `UpNextCard` and `HomeAppointmentsTile` build off the
// `bookingPath` the host passes them. No card points at the bare base — step 3's own `to` is the
// TELEMEDICINE_PATH resolver above, which is what `initialStageContent` builds "Book appointment"
// from.
//
// ⚠ `/telemedicine/booking/:id/forms` SELF-GATES, which is why there is no second constant for an
// id-upload step: the page renders `PhotoIdUpload` until `getPhotoIdOnFile` reports `onFile` and
// then falls through to the questionnaire. Home v7's `ap-needsid` state is the same card with a
// different CTA label, so one `scheduled-forms-incomplete` card covers both and the page decides
// what the member sees on arrival (CONAPP-343). Nothing here reads `getPhotoIdOnFile`.
export const TELEMEDICINE_BOOKING_PATH = "/telemedicine/booking"
// The host's `Paths.TELEMEDICINE_APPOINTMENTS`, and a DESTINATION rather than a base: the booked
// visit's **View appointment** goes straight here, with nothing interpolated. The page lists the
// member's own appointments, so it needs no id to name which one — see `scheduledStageContent`.
//
// It is also where `@internal/telemedicine`'s `HomeAppointmentsTile` sends its "View all" and "View
// details", through an `appointmentsPath` prop the host fills from the same enum member. Two
// surfaces, one destination, and no shared constant between the packages — the tile takes a prop
// because it cannot import from `#/` either.
export const TELEMEDICINE_APPOINTMENTS_PATH = "/appointments"
// The Academy library ROOT, and since CONAPP-296 a base rather than a destination: the 26 Academy
// steps each build their own per-resource path from it (`plan-steps.tsx`'s `academyArticle` /
// `academyGuide` / `academySubcategory`). No card points at the bare root any more.
//
// ⚠ Keep it the only `/read` prefix in this file. `PlanStepSourceLabel` decides whether to draw the
// "Carrot Academy" label by testing a step's destination against it with `startsWith`, so a
// non-Academy destination added under `/read` would start claiming to be Academy content.
export const CARROT_ACADEMY_PATH = "/read"
// The host's "Find care and services" nav item (`ProviderFinderNavItem` → `Paths.PROVIDER_FINDER`).
export const FIND_CARE_PATH = "/provider-finder"
export const CARROT_RX_PATH = "/carrot-rx"
// Where the four at-home test steps send members (`Paths.EXCLUSIVE_DISCOUNTS`). The tests are sold
// through the Carrot Collective rather than linked directly: each vendor's CTA is a dynamic POST
// built from Contentful, so there is no static vendor URL for a step to point at.
export const EXCLUSIVE_DISCOUNTS_PATH = "/exclusive-discounts"
